import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import CookiesBanner from './CookiesBanner';
import StickyMobileBar from './StickyMobileBar';
import { useReveal } from '../hooks/useReveal';

interface Crumb {
  name: string;
  href?: string;
}

interface PageLayoutProps {
  children: React.ReactNode;
  crumbs?: Crumb[];
  /** Hero interior: label + título display */
  title: string;
  titleEm?: string;
  label: string;
  lead?: string;
}

/**
 * Shell de páginas interiores: header + hero editorial + contenido + footer.
 */
const PageLayout = ({ children, crumbs = [], title, titleEm, label, lead }: PageLayoutProps) => {
  useReveal();

  return (
    <div id="top" className="relative min-h-screen bg-paper text-ink grain">
      <Header />
      <main>
        {/* Hero interior */}
        <div className="pt-[120px] md:pt-[150px] pb-14 md:pb-20 border-b border-line relative overflow-hidden">
          <div className="absolute inset-0 blueprint-grid pointer-events-none" aria-hidden="true" />
          <div className="relative max-w-[1400px] mx-auto px-6">
            <nav aria-label="Migas de pan" className="flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
              <Link to="/" className="hover:text-clay transition-colors">Inicio</Link>
              {crumbs.map((c, i) => (
                <React.Fragment key={i}>
                  <ChevronRight size={13} className="text-ink-faint/60" />
                  {c.href ? (
                    <Link to={c.href} className="hover:text-clay transition-colors">{c.name}</Link>
                  ) : (
                    <span className="text-ink" aria-current="page">{c.name}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>

            <p className="label mt-10 reveal">{label}</p>
            <h1 className="reveal font-display font-black uppercase tracking-[-0.025em] text-[clamp(2.4rem,6.5vw,5rem)] leading-[0.98] mt-5 max-w-4xl">
              {title}
              {titleEm && <em className="font-serifit normal-case font-normal text-clay"> {titleEm}</em>}
            </h1>
            {lead && (
              <p className="reveal mt-6 text-ink-soft text-lg leading-relaxed max-w-2xl">{lead}</p>
            )}
          </div>
        </div>

        {children}
      </main>
      <Footer />
      <StickyMobileBar />
      <CookiesBanner />
    </div>
  );
};

export default PageLayout;
